import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X } from 'lucide-react';
import { cn } from '#client/lib/cn';
import { Button } from './Button';

interface SortableSelectionProps<T extends { id: number }> {
  /** Every item a selected id may refer to. Ids with no item are skipped. */
  items: readonly T[];
  /** The selection, in order. */
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  label: (item: T) => string;
  emptyState?: string | undefined;
  /** Read-only: the order still shows, but cannot change. */
  disabled?: boolean | undefined;
}

/**
 * A selection whose order is part of the data — dragged into place by the grip,
 * or with the keyboard (Space on the grip, then the arrow keys).
 *
 * Pairs with `CheckboxPicker`, which decides membership; this decides order.
 */
export function SortableSelection<T extends { id: number }>({
  items,
  selectedIds,
  onChange,
  label,
  emptyState = 'Nothing selected',
  disabled = false,
}: SortableSelectionProps<T>) {
  const sensors = useSensors(
    // A small travel threshold so a click on the grip is not a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byId = new Map(items.map((item) => [item.id, item]));
  const selected = selectedIds.flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = selectedIds.indexOf(Number(active.id));
    const to = selectedIds.indexOf(Number(over.id));
    onChange(arrayMove(selectedIds, from, to));
  }

  if (selected.length === 0) return <p className="c-sortable__empty">{emptyState}</p>;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
        <ol className="c-sortable">
          {selected.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              position={index + 1}
              label={label(item)}
              disabled={disabled}
              onRemove={() => onChange(selectedIds.filter((id) => id !== item.id))}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  position,
  label,
  disabled,
  onRemove,
}: {
  id: number;
  position: number;
  label: string;
  disabled: boolean;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  return (
    <li
      ref={setNodeRef}
      className={cn('c-sortable__item', isDragging && 'c-sortable__item--dragging')}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      {!disabled && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="c-sortable__handle"
          aria-label={`Reorder ${label}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
      )}
      <span className="c-sortable__position">{position}.</span>
      <span className="c-sortable__label">{label}</span>
      {!disabled && (
        <Button
          variant="ghost"
          size="sm"
          icon={<X />}
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        />
      )}
    </li>
  );
}
